// 블록 ② 오디언스 스트립 — 합산 팔로워 + 채널별 구독(자동 수집 도트).
//
// 첫 셀은 themeColor 파생 틴트(없으면 중립 bg-soft), 나머지는 채널.
// 채널 수(0~3)에 맞춰 컬럼 수 조정(graceful). 채널이 0개면 합계는 0이 아니라 "—"다.
//
// 도트는 원래 aria-hidden 장식이라 스크린리더에 존재하지 않았다 → 라벨과 텍스트 상태를 부여한다.
//   도트가 뜻하는 건 "**게시물** 자동 수집 대상"(축 A' = 기존 linked와 값 동일)이고, 수집 신선도가 아니다.
//   ⚠️ 툴팁의 수집일은 `lastCollectedAt` = 축 A(채널 지표)다. 두 축이 한 칸에 붙으므로 문구로 구분한다
//      (사이드바 배지의 autoCollect는 축 A라 값이 다를 수 있다 — 혼용 금지).
//   신선도 배지와 의미 축이 갈리므로 **도트 색은 건드리지 않는다**(기존 핑크 유지).
import type { ChannelSubs } from '../../creatorsApi'
import { InfoTip } from '../InfoTip'
import { PLATFORM_LABEL, formatCount, formatIsoDate, hexA } from './format'

interface Props {
  totalSubscribers: number
  channels: ChannelSubs[]
  themeColor: string
}

// 기획서 §1 긍정 프레임 — 이 화면은 크리에이터를 브랜드에 제안하는 세일즈 카드다.
//   툴팁은 **집계 기준만** 밝히고 값을 깎아내리는 표현("실제 도달보다 크다" 류)은 쓰지 않는다.
const TOTAL_TIP = '보유 전 채널 팔로워를 합산한 값입니다.'

export function AudienceStrip({ totalSubscribers, channels, themeColor }: Props) {
  const cols = 1 + channels.length
  const tintTop = hexA(themeColor, 0.24)
  const totalTint = tintTop ? `linear-gradient(180deg, ${tintTop} 0%, ${hexA(themeColor, 0.06)} 100%)` : undefined
  // 합산에 실제로 들어간 채널 수 — 팔로워가 결측인 채널은 합계에서 빠졌다는 사실을 표기한다.
  const counted = channels.filter((c) => c.subscribers != null && Number.isFinite(c.subscribers)).length
  const scopeText =
    channels.length === 0
      ? '채널 없음'
      : counted === channels.length
        ? `${channels.length}개 채널 합산`
        : `${channels.length}개 중 ${counted}개 합산`

  return (
    <div className="overflow-x-auto border border-t-0 border-line-hairline bg-paper">
      <div
        className="grid min-w-[560px] gap-px bg-line-hairline"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        <div
          className={totalTint ? 'p-[20px_22px]' : 'bg-surface-soft p-[20px_22px]'}
          style={totalTint ? { background: totalTint } : undefined}
        >
          <div className="flex items-center gap-1 font-text text-[11px] text-fg-3">
            합산 팔로워
            <InfoTip text={TOTAL_TIP} label="합산 팔로워 집계 기준" />
          </div>
          <div
            className={[
              'mt-2 whitespace-nowrap font-display text-[40px] font-bold leading-none tabular-nums',
              channels.length === 0 ? 'text-ink-400' : 'text-fg-1',
            ].join(' ')}
          >
            {channels.length === 0 ? '—' : formatCount(totalSubscribers)}
          </div>
          <div className="mt-[3px] font-text text-[11px] text-fg-3">{scopeText}</div>
        </div>
        {channels.map((ch) => {
          // autoCollect(정직한 이름) 우선, 미도달이면 기존 linked 폴백 — 값은 동일하다.
          const autoCollect = ch.autoCollect ?? ch.linked
          const collected = formatIsoDate(ch.lastCollectedAt)
          return (
            <div key={`${ch.platform}-${ch.handle}`} className="min-w-0 bg-surface-base p-[20px_22px]">
              <div className="flex items-center gap-2">
                <span className="whitespace-nowrap font-text text-[11px] text-fg-3">{PLATFORM_LABEL[ch.platform]}</span>
                {autoCollect && (
                  <span
                    className="inline-block h-[5px] w-[5px] rounded-full bg-brand-pink"
                    role="img"
                    aria-label="게시물 자동 수집 대상"
                  />
                )}
              </div>
              <div className="mt-2 whitespace-nowrap font-display text-[28px] font-medium leading-none tabular-nums text-fg-1">
                {formatCount(ch.subscribers)}
              </div>
              <div
                className="mt-[3px] font-text text-[11px] text-fg-3"
                title={
                  autoCollect
                    ? `게시물 자동 수집 대상${collected ? ` · 채널 지표 수집 ${collected}` : ''}`
                    : '게시물 자동 수집 대상이 아닌 채널 — 팔로워 수만 등록되어 있습니다'
                }
              >
                {/* 무엇의 수집인지 밝힌다 — 숫자는 구독자인데 이 라벨은 **게시물** 수집 여부다.
                    "자동 수집"만 쓰면 구독자 수가 자동 수집된다는 뜻으로 읽히는데, 구독자 갱신은
                    다른 축(status_auto_update_date)이라 대상이 아니어도 정상 갱신된다. */}
                Subscribers · {autoCollect ? '게시물 수집' : '구독자만'}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
